import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Header } from "../components/Header";
import { 
  Search, ChevronLeft, ChevronRight, Home, FileText, 
  Clock, Building2, ArrowRight, Megaphone, Download,
  X, CheckCircle, AlertCircle, Tag, Hash
} from "lucide-react";
import { useIsMobile } from "../components/ui/use-mobile";

interface Announcement {
  id: number;
  projectNo: string;
  projectName: string;
  projectNature: string;
  biddingUnit: string;
  openBidTime: string;
  publishTime: string;
  isBidopening: number;
}

interface BidOrder {
  id: number;
  supplierName: string;
  uploadTime: string | null;
  bidFile: string | null;
  downloaded: boolean;
  downloadTime: string | null;
}

// 获取服务器时间
async function getServerTime(): Promise<Date> {
  try {
    const res = await fetch('/api/server-time');
    const data = await res.json();
    if (data.ok && data.time) {
      return new Date(data.time);
    }
  } catch (e) {
    console.error('获取服务器时间失败', e);
  }
  return new Date();
}

// 倒计时组件
function Countdown({ openBidTime, serverTimeOffset, onTimeUp }: { 
  openBidTime: string; 
  serverTimeOffset: number;
  onTimeUp: () => void;
}) {
  const [timeLeft, setTimeLeft] = useState<{ hours: number; minutes: number; seconds: number } | null>(null);
  const [isTimeUp, setIsTimeUp] = useState(false);

  useEffect(() => {
    const targetTime = new Date(openBidTime).getTime();
    
    const updateCountdown = () => {
      const now = Date.now() + serverTimeOffset;
      const diff = targetTime - now;
      
      if (diff <= 0) {
        setIsTimeUp(true);
        setTimeLeft(null);
        onTimeUp();
        return;
      }
      
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      
      setTimeLeft({ hours, minutes, seconds });
    };
    
    updateCountdown();
    const timer = setInterval(updateCountdown, 1000);
    
    return () => clearInterval(timer);
  }, [openBidTime, serverTimeOffset, onTimeUp]);

  if (isTimeUp) {
    return (
      <span style={{ color: '#10b981', fontWeight: 600 }}>已到</span>
    );
  }

  if (!timeLeft) {
    return <span>计算中...</span>;
  }

  // 检测是否为移动端（通过检查父组件传入的isMobile，如果没有则使用窗口宽度判断）
  const isMobile = window.innerWidth < 768;
  
  return (
    <span style={{ color: '#f59e0b', fontWeight: 600, fontSize: isMobile ? '12px' : '14px', wordBreak: 'keep-all', whiteSpace: 'nowrap' }}>
      {isMobile ? (
        <>
          {String(timeLeft.hours).padStart(2, '0')}:{String(timeLeft.minutes).padStart(2, '0')}:{String(timeLeft.seconds).padStart(2, '0')}
        </>
      ) : (
        <>
          还有{String(timeLeft.hours).padStart(2, '0')}时{String(timeLeft.minutes).padStart(2, '0')}分{String(timeLeft.seconds).padStart(2, '0')}秒开标
        </>
      )}
    </span>
  );
}

// 下载投标文件弹窗
function DownloadModal({ 
  visible, 
  projectNo, 
  projectName,
  expertId,
  onClose 
}: { 
  visible: boolean; 
  projectNo: string;
  projectName: string;
  expertId: number;
  onClose: () => void;
}) {
  const isMobile = useIsMobile();
  const [orders, setOrders] = useState<BidOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState<number | null>(null);

  const fetchOrders = useCallback(async () => {
    if (!projectNo || !expertId) return;
    
    setLoading(true);
    try {
      const res = await fetch(`/api/expert/bid-orders?project_no=${encodeURIComponent(projectNo)}&expert_id=${expertId}`);
      const data = await res.json();
      if (data.ok) {
        // 按订单ID去重，确保每个订单只显示一次
        const uniqueOrders = (data.orders || []).reduce((acc: BidOrder[], order: BidOrder) => {
          if (!acc.find(o => o.id === order.id)) {
            acc.push(order);
          }
          return acc;
        }, []);
        setOrders(uniqueOrders);
      }
    } catch (e) {
      console.error('获取投标文件列表失败', e);
    } finally {
      setLoading(false);
    }
  }, [projectNo, expertId]);

  useEffect(() => {
    if (visible) {
      fetchOrders();
    }
  }, [visible, fetchOrders]);

  // 从URL中提取文件名
  const getFileNameFromUrl = (url: string | null): string => {
    if (!url) return '投标文件';
    const parts = url.split('/');
    return parts[parts.length - 1] || '投标文件';
  };

  const handleDownload = async (order: BidOrder) => {
    if (!order.bidFile) {
      alert('该供应商暂未上传投标文件');
      return;
    }
    
    setDownloading(order.id);
    try {
      // 先记录下载
      await fetch('/api/expert/record-download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          expert_id: expertId,
          order_id: order.id
        })
      });
      
      // 从URL中提取源文件名
      const sourceFileName = getFileNameFromUrl(order.bidFile);
      // 生成下载文件名：项目编号_源文件名
      const downloadFileName = `${projectNo}_${sourceFileName}`;
      
      // 下载文件
      const link = document.createElement('a');
      link.href = order.bidFile;
      link.download = downloadFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // 刷新列表
      await fetchOrders();
    } catch (e) {
      console.error('下载失败', e);
      alert('下载失败，请重试');
    } finally {
      setDownloading(null);
    }
  };

  if (!visible) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: isMobile ? '16px' : '0'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: isMobile ? '12px' : '16px',
        width: isMobile ? '100%' : '50vw',
        maxWidth: '800px',
        maxHeight: isMobile ? '90vh' : '80vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
      }}>
        {/* 弹窗头部 */}
        <div style={{
          padding: isMobile ? '16px' : '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(to right, #f8fafc, white)'
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
              下载投标文件
            </h3>
            <p style={{ fontSize: isMobile ? '12px' : '13px', color: '#6b7280', wordBreak: 'break-word' }}>
              {projectName}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: isMobile ? '32px' : '36px',
              height: isMobile ? '32px' : '36px',
              borderRadius: '8px',
              border: 'none',
              backgroundColor: '#f3f4f6',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              marginLeft: '12px'
            }}
          >
            <X style={{ width: isMobile ? '18px' : '20px', height: isMobile ? '18px' : '20px', color: '#6b7280' }} />
          </button>
        </div>

        {/* 表格头部 */}
        {!isMobile && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1.5fr 1fr 1fr',
            padding: '12px 24px',
            backgroundColor: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
            fontSize: '13px',
            fontWeight: 600,
            color: '#6b7280'
          }}>
            <span>供应商名称</span>
            <span>上传时间</span>
            <span>投标文件下载</span>
            <span>下载状态</span>
          </div>
        )}

        {/* 表格内容 */}
        <div style={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <div style={{ padding: isMobile ? '24px' : '40px', textAlign: 'center', color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>
              加载中...
            </div>
          ) : orders.length === 0 ? (
            <div style={{ padding: isMobile ? '24px' : '40px', textAlign: 'center', color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>
              暂无供应商投标
            </div>
          ) : (
            orders.map((order, index) => (
              isMobile ? (
                // 手机端卡片布局
                <div
                  key={order.id}
                  style={{
                    padding: '16px',
                    borderBottom: index < orders.length - 1 ? '1px solid #f3f4f6' : 'none',
                    fontSize: '14px'
                  }}
                >
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>供应商名称</div>
                    <div style={{ color: '#1f2937', fontWeight: 500, fontSize: '14px' }}>{order.supplierName}</div>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>上传时间</div>
                    <div style={{ color: '#6b7280', fontSize: '13px' }}>
                      {order.bidFile && order.uploadTime ? new Date(order.uploadTime).toLocaleString('zh-CN') : '-'}
                    </div>
                  </div>
                  <div style={{ marginBottom: '12px' }}>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>投标文件下载</div>
                    <div>
                      {order.bidFile ? (
                        <button
                          onClick={() => handleDownload(order)}
                          disabled={downloading === order.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '4px',
                            padding: '8px 16px',
                            width: '100%',
                            backgroundColor: downloading === order.id ? '#e5e7eb' : '#3b82f6',
                            color: downloading === order.id ? '#9ca3af' : 'white',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '13px',
                            cursor: downloading === order.id ? 'not-allowed' : 'pointer'
                          }}
                        >
                          <Download style={{ width: '14px', height: '14px' }} />
                          {downloading === order.id ? '下载中...' : '下载'}
                        </button>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '13px' }}>未上传</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>下载状态</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {order.downloaded ? (
                        <>
                          <CheckCircle style={{ width: '16px', height: '16px', color: '#10b981' }} />
                          <span style={{ color: '#10b981', fontSize: '13px' }}>已下载</span>
                        </>
                      ) : (
                        <>
                          <AlertCircle style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
                          <span style={{ color: '#9ca3af', fontSize: '13px' }}>未下载</span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                // 桌面端表格布局
                <div
                  key={order.id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2fr 1.5fr 1fr 1fr',
                    padding: '16px 24px',
                    borderBottom: index < orders.length - 1 ? '1px solid #f3f4f6' : 'none',
                    alignItems: 'center',
                    fontSize: '14px'
                  }}
                >
                  <span style={{ color: '#1f2937', fontWeight: 500 }}>{order.supplierName}</span>
                  <span style={{ color: '#6b7280' }}>
                    {order.bidFile && order.uploadTime ? new Date(order.uploadTime).toLocaleString('zh-CN') : '-'}
                  </span>
                  <div>
                    {order.bidFile ? (
                      <button
                        onClick={() => handleDownload(order)}
                        disabled={downloading === order.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '6px 12px',
                          backgroundColor: downloading === order.id ? '#e5e7eb' : '#3b82f6',
                          color: downloading === order.id ? '#9ca3af' : 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '13px',
                          cursor: downloading === order.id ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <Download style={{ width: '14px', height: '14px' }} />
                        {downloading === order.id ? '下载中...' : '下载'}
                      </button>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: '13px' }}>未上传</span>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {order.downloaded ? (
                      <>
                        <CheckCircle style={{ width: '16px', height: '16px', color: '#10b981' }} />
                        <span style={{ color: '#10b981' }}>已下载</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle style={{ width: '16px', height: '16px', color: '#9ca3af' }} />
                        <span style={{ color: '#9ca3af' }}>未下载</span>
                      </>
                    )}
                  </div>
                </div>
              )
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default function ExpertEvaluationPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [searchKeyword, setSearchKeyword] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hoveredItem, setHoveredItem] = useState<number | null>(null);
  const [serverTimeOffset, setServerTimeOffset] = useState(0);
  const [timeUpItems, setTimeUpItems] = useState<Set<number>>(new Set());
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState<Announcement | null>(null);
  const [expertId, setExpertId] = useState<number | null>(null);
  const pageSize = 10;

  const typeConfig = { color: "#3b82f6", bgColor: "#eff6ff" };

  // 获取专家ID
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.id) {
          setExpertId(user.id);
        }
      } catch (e) {
        console.error('解析用户信息失败', e);
      }
    }
  }, []);

  // 初始化时获取服务器时间偏移
  useEffect(() => {
    async function syncTime() {
      const serverTime = await getServerTime();
      const offset = serverTime.getTime() - Date.now();
      setServerTimeOffset(offset);
    }
    syncTime();
    // 每分钟同步一次
    const timer = setInterval(syncTime, 60000);
    return () => clearInterval(timer);
  }, []);

  // 格式化日期
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const h = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    const s = String(date.getSeconds()).padStart(2, '0');
    return `${y}-${m}-${d} ${h}:${min}:${s}`;
  };

  // 获取当天开标的公告列表
  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: pageSize.toString(),
      });
      
      if (searchKeyword) {
        params.append('keyword', searchKeyword);
      }

      const response = await fetch(`/api/expert/today-bids?${params}`);
      const data = await response.json();

      if (data.ok) {
        setAnnouncements(data.data.map((item: any) => ({
          id: item.id,
          projectNo: item.project_no,
          projectName: item.project_name,
          projectNature: item.project_nature || '-',
          biddingUnit: item.bidding_unit,
          openBidTime: item.open_bid_time,
          publishTime: formatDate(item.publish_time) || '',
          isBidopening: item.is_bidopening !== null && item.is_bidopening !== undefined ? item.is_bidopening : 0
        })));
        setTotal(data.total);
      }
    } catch (error) {
      console.error('获取公告列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 搜索时重置页码
  useEffect(() => {
    setCurrentPage(1);
  }, [searchKeyword]);

  // 加载数据
  useEffect(() => {
    fetchAnnouncements();
  }, [currentPage, searchKeyword]);

  // 分页数据
  const totalPages = Math.ceil(total / pageSize);

  // 处理时间到达
  const handleTimeUp = (id: number) => {
    setTimeUpItems(prev => new Set([...prev, id]));
  };

  // 打开下载弹窗
  const handleOpenDownload = (announcement: Announcement) => {
    setSelectedAnnouncement(announcement);
    setModalVisible(true);
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      <Header />

      {/* 页面横幅 */}
      <div style={{
        background: 'linear-gradient(135deg, #003c8c 0%, #0050b8 50%, #0066cc 100%)',
        padding: isMobile ? '32px 0' : '60px 0',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* 装饰元素 */}
        <div style={{
          position: 'absolute',
          top: '-80px',
          right: '-50px',
          width: '400px',
          height: '400px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.05)',
        }} />
        <div style={{
          position: 'absolute',
          bottom: '-100px',
          left: '5%',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.03)',
        }} />
        
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '12px' : '16px', marginBottom: isMobile ? '12px' : '16px' }}>
            <div style={{
              width: isMobile ? '40px' : '56px',
              height: isMobile ? '40px' : '56px',
              borderRadius: '16px',
              backgroundColor: 'rgba(255,255,255,0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FileText style={{ width: isMobile ? '20px' : '28px', height: isMobile ? '20px' : '28px', color: 'white' }} />
            </div>
            <h1 style={{ 
              fontSize: isMobile ? '24px' : '36px', 
              fontWeight: 700, 
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.1)'
            }}>
              自动电子评标
            </h1>
          </div>
          <p style={{ fontSize: isMobile ? '14px' : '16px', color: 'rgba(255,255,255,0.8)', maxWidth: '600px' }}>
            今日开标项目列表，评审人员可在此下载投标文件进行评审
          </p>
        </div>
      </div>

      {/* 面包屑导航 */}
      <div style={{ backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' }}>
        <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '12px 0' : '16px 0' }}>
          <nav style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px', fontSize: isMobile ? '12px' : '14px', flexWrap: 'wrap' }}>
            <Link
              to="/"
              style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6b7280', textDecoration: 'none', transition: 'color 0.2s' }}
              onMouseOver={(e) => e.currentTarget.style.color = '#003c8c'}
              onMouseOut={(e) => e.currentTarget.style.color = '#6b7280'}
            >
              <Home style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
              首页
            </Link>
            <ChevronRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px', color: '#d1d5db' }} />
            <span style={{ color: '#003c8c', fontWeight: 500 }}>自动电子评标</span>
          </nav>
        </div>
      </div>

      {/* 主内容区域 */}
      <div style={{ width: isMobile ? '90%' : '80%', margin: '0 auto', padding: isMobile ? '20px 0' : '40px 0' }}>
        {/* 搜索和列表区域 */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: isMobile ? '12px' : '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          overflow: 'hidden'
        }}>
          {/* 搜索栏 */}
          <div style={{
            padding: isMobile ? '16px' : '24px 32px',
            borderBottom: '1px solid #f3f4f6',
            background: 'linear-gradient(to right, #fafbfc, white)'
          }}>
            <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'stretch' : 'center', justifyContent: 'space-between', gap: isMobile ? '12px' : '0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', flexWrap: 'wrap' }}>
                <div style={{
                  width: '4px',
                  height: isMobile ? '20px' : '24px',
                  backgroundColor: typeConfig.color,
                  borderRadius: '2px'
                }} />
                <h3 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 600, color: '#1f2937' }}>
                  今日开标项目
                </h3>
                <span style={{
                  padding: isMobile ? '3px 10px' : '4px 12px',
                  backgroundColor: typeConfig.bgColor,
                  color: typeConfig.color,
                  fontSize: isMobile ? '11px' : '13px',
                  fontWeight: 500,
                  borderRadius: '20px'
                }}>
                  共 {total} 条
                </span>
              </div>
              
              {/* 搜索框 */}
              <div style={{ position: 'relative', width: isMobile ? '100%' : '320px' }}>
                <input
                  type="text"
                  placeholder="搜索项目编号或项目名称..."
                  value={searchKeyword}
                  onChange={(e) => setSearchKeyword(e.target.value)}
                  style={{
                    width: '100%',
                    height: isMobile ? '40px' : '44px',
                    paddingLeft: isMobile ? '40px' : '48px',
                    paddingRight: '16px',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    fontSize: isMobile ? '13px' : '14px',
                    outline: 'none',
                    transition: 'all 0.2s'
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = typeConfig.color;
                    e.currentTarget.style.boxShadow = `0 0 0 4px ${typeConfig.color}15`;
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
                <Search style={{ 
                  position: 'absolute', 
                  left: isMobile ? '12px' : '16px', 
                  top: '50%', 
                  transform: 'translateY(-50%)', 
                  width: isMobile ? '18px' : '20px', 
                  height: isMobile ? '18px' : '20px', 
                  color: '#9ca3af' 
                }} />
              </div>
            </div>
          </div>

          {/* 公告列表 */}
          {loading ? (
            <div style={{ padding: isMobile ? '40px 20px' : '80px 0', textAlign: 'center', color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>
              加载中...
            </div>
          ) : announcements.length > 0 ? (
            <div>
              {announcements.map((item, index) => {
                const isTimeUp = timeUpItems.has(item.id) || new Date(item.openBidTime).getTime() <= Date.now() + serverTimeOffset;
                
                return (
                  <div
                    key={item.id}
                    onMouseEnter={() => !isMobile && setHoveredItem(item.id)}
                    onMouseLeave={() => !isMobile && setHoveredItem(null)}
                    style={{
                      padding: isMobile ? '16px' : '24px 32px',
                      borderBottom: index < announcements.length - 1 ? '1px solid #f3f4f6' : 'none',
                      background: hoveredItem === item.id && !isMobile
                        ? `linear-gradient(to right, ${typeConfig.bgColor}, transparent)` 
                        : 'white',
                      transition: 'all 0.25s ease'
                    }}
                  >
                    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '16px' : '24px' }}>
                      {/* 左侧图标 */}
                      <div style={{
                        width: isMobile ? '40px' : '56px',
                        height: isMobile ? '40px' : '56px',
                        borderRadius: '14px',
                        background: hoveredItem === item.id && !isMobile
                          ? `linear-gradient(135deg, ${typeConfig.color}, ${typeConfig.color}cc)`
                          : typeConfig.bgColor,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        transition: 'all 0.3s ease'
                      }}>
                        <Megaphone style={{ 
                          width: isMobile ? '20px' : '26px', 
                          height: isMobile ? '20px' : '26px', 
                          color: hoveredItem === item.id && !isMobile ? 'white' : typeConfig.color 
                        }} />
                      </div>

                      {/* 中间内容 */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{ 
                          fontSize: isMobile ? '15px' : '17px', 
                          fontWeight: 600, 
                          color: hoveredItem === item.id && !isMobile ? typeConfig.color : '#1f2937', 
                          marginBottom: isMobile ? '6px' : '8px',
                          wordBreak: 'break-word',
                          transition: 'color 0.2s'
                        }}>
                          {item.projectName}
                        </h3>
                        {/* 第二行：项目编号和项目性质 */}
                        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? '8px' : '24px', fontSize: isMobile ? '12px' : '14px', color: '#6b7280', marginBottom: isMobile ? '6px' : '8px', flexWrap: 'wrap' }}>
                          <span style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '6px' }}>
                            <Hash style={{ width: isMobile ? '12px' : '14px', height: isMobile ? '12px' : '14px', flexShrink: 0 }} />
                            {item.projectNo}
                          </span>
                          <span style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '6px' }}>
                            <Tag style={{ width: isMobile ? '12px' : '14px', height: isMobile ? '12px' : '14px', flexShrink: 0 }} />
                            {item.projectNature}
                          </span>
                        </div>
                        {/* 第三行：招标单位 */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '6px', fontSize: isMobile ? '12px' : '14px', color: '#6b7280', flexWrap: 'wrap' }}>
                          <Building2 style={{ width: isMobile ? '12px' : '14px', height: isMobile ? '12px' : '14px', flexShrink: 0 }} />
                          <span style={{ wordBreak: 'break-word' }}>{item.biddingUnit}</span>
                        </div>
                      </div>

                      {/* 右侧：开标时间倒计时 */}
                      <div style={{ 
                        flexShrink: 0, 
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        alignItems: isMobile ? 'stretch' : 'center',
                        gap: isMobile ? '12px' : '16px',
                        width: isMobile ? '100%' : 'auto'
                      }}>
                        <div style={{ textAlign: isMobile ? 'left' : 'right', flex: isMobile ? '1' : 'none' }}>
                          <div style={{ fontSize: isMobile ? '11px' : '12px', color: '#9ca3af', marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Clock style={{ width: isMobile ? '11px' : '12px', height: isMobile ? '11px' : '12px' }} />
                            开标时间
                          </div>
                          <div style={{ fontSize: isMobile ? '12px' : '14px' }}>
                            <Countdown 
                              openBidTime={item.openBidTime} 
                              serverTimeOffset={serverTimeOffset}
                              onTimeUp={() => handleTimeUp(item.id)}
                            />
                          </div>
                        </div>

                        {/* 下载投标文件按钮或解密中提示（仅在开标时间到达后显示） */}
                        {isTimeUp && (
                          item.isBidopening === 1 ? (
                            <button
                              onClick={() => handleOpenDownload(item)}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: isMobile ? '6px' : '6px',
                                padding: isMobile ? '8px 16px' : '10px 20px',
                                border: `2px solid ${typeConfig.color}`,
                                borderRadius: '10px',
                                backgroundColor: hoveredItem === item.id && !isMobile ? typeConfig.color : 'white',
                                color: hoveredItem === item.id && !isMobile ? 'white' : typeConfig.color,
                                fontSize: isMobile ? '13px' : '14px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                flexShrink: 0,
                                width: isMobile ? '100%' : 'auto',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              <Download style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
                              下载投标文件
                            </button>
                          ) : (
                            <span style={{
                              padding: isMobile ? '8px 16px' : '10px 20px',
                              fontSize: isMobile ? '12px' : '14px',
                              fontWeight: 500,
                              color: '#f59e0b',
                              flexShrink: 0,
                              textAlign: isMobile ? 'center' : 'left',
                              display: 'block',
                              width: isMobile ? '100%' : 'auto'
                            }}>
                              解密中，请耐心等待！
                            </span>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ padding: isMobile ? '40px 20px' : '80px 0', textAlign: 'center' }}>
              <div style={{ 
                width: isMobile ? '80px' : '100px', 
                height: isMobile ? '80px' : '100px', 
                margin: '0 auto 24px', 
                backgroundColor: '#f3f4f6', 
                borderRadius: '50%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center' 
              }}>
                <Search style={{ width: isMobile ? '32px' : '40px', height: isMobile ? '32px' : '40px', color: '#9ca3af' }} />
              </div>
              <p style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '16px' }}>今日暂无开标项目</p>
              <p style={{ color: '#9ca3af', fontSize: isMobile ? '12px' : '14px', marginTop: '8px' }}>请稍后再来查看</p>
            </div>
          )}

          {/* 分页 */}
          {totalPages > 1 && (
            <div style={{ 
              padding: isMobile ? '16px' : '24px 32px', 
              borderTop: '1px solid #f3f4f6', 
              display: 'flex', 
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'stretch' : 'center', 
              justifyContent: 'space-between',
              background: 'linear-gradient(to right, #fafbfc, white)',
              gap: isMobile ? '12px' : '0'
            }}>
              <span style={{ fontSize: isMobile ? '12px' : '14px', color: '#6b7280', textAlign: isMobile ? 'center' : 'left' }}>
                共 <span style={{ color: typeConfig.color, fontWeight: 600 }}>{total}</span> 条记录，
                第 <span style={{ fontWeight: 600 }}>{currentPage}</span>/<span style={{ fontWeight: 600 }}>{totalPages}</span> 页
              </span>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'center' : 'flex-end', gap: isMobile ? '6px' : '8px', flexWrap: 'wrap' }}>
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: isMobile ? '6px 12px' : '8px 16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    color: currentPage === 1 ? '#d1d5db' : '#374151',
                    fontSize: isMobile ? '12px' : '14px',
                    cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  <ChevronLeft style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
                  {isMobile ? '上一页' : '上一页'}
                </button>

                {Array.from({ length: Math.min(totalPages, isMobile ? 3 : 5) }, (_, i) => {
                  let page;
                  const maxPages = isMobile ? 3 : 5;
                  if (totalPages <= maxPages) {
                    page = i + 1;
                  } else if (currentPage <= Math.ceil(maxPages / 2)) {
                    page = i + 1;
                  } else if (currentPage >= totalPages - Math.floor(maxPages / 2)) {
                    page = totalPages - maxPages + 1 + i;
                  } else {
                    page = currentPage - Math.floor(maxPages / 2) + i;
                  }
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      style={{
                        width: isMobile ? '32px' : '36px',
                        height: isMobile ? '32px' : '36px',
                        border: 'none',
                        borderRadius: '8px',
                        backgroundColor: page === currentPage ? typeConfig.color : 'white',
                        color: page === currentPage ? 'white' : '#374151',
                        fontSize: isMobile ? '12px' : '14px',
                        fontWeight: page === currentPage ? 600 : 400,
                        cursor: 'pointer',
                        boxShadow: page === currentPage ? `0 4px 12px ${typeConfig.color}40` : '0 1px 3px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s'
                      }}
                    >
                      {page}
                    </button>
                  );
                })}

                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: isMobile ? '6px 12px' : '8px 16px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    backgroundColor: 'white',
                    color: currentPage === totalPages ? '#d1d5db' : '#374151',
                    fontSize: isMobile ? '12px' : '14px',
                    cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {isMobile ? '下一页' : '下一页'}
                  <ChevronRight style={{ width: isMobile ? '14px' : '16px', height: isMobile ? '14px' : '16px' }} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 下载投标文件弹窗 */}
      {selectedAnnouncement && expertId && (
        <DownloadModal
          visible={modalVisible}
          projectNo={selectedAnnouncement.projectNo}
          projectName={selectedAnnouncement.projectName}
          expertId={expertId}
          onClose={() => {
            setModalVisible(false);
            setSelectedAnnouncement(null);
          }}
        />
      )}
    </div>
  );
}
